var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useRef } from 'react';
import { Swap, getAutoSlippage } from 'ui-kit';
import { getIcon } from '../utils/getIcon';
import { getCoinDisplayName, wbtc_layer0_coinType } from '../utils/coinDisplayUtils';
import { useBalances } from '../context/BalanceContext';
import { useCurrentAccount } from '@mysten/dapp-kit-react';
import { useCurrentWallet, useSignAndExecuteTransaction } from '../hooks/walletCompat';
import { useAddressBalanceGuard } from '../hooks/useAddressBalanceGuard';
import { createAlphaFiSDK } from '../utils/networkUtils';
import { useSubscription } from '../hooks/useSubscription';
import { normalizeAmount, convertToDecimal, calculateMinTokensReceived, } from '../utils/zapSwapUtils';
import { EXCLUDED_TOKENS } from '../utils/tokenConstants';
const highlightedTokenSymbols = ['USDSUI', 'USDC', 'suiUSDT', 'WBTC', 'WAL', 'DEEP'];
const SwapModal = ({ isOpen, onClose, onNotification, loading, setLoading, walletConnectFunctionality, isWalletConnected, theme, initialReceiveSymbol, }) => {
    const [payToken, setPayToken] = useState();
    const [receiveToken, setReceiveToken] = useState();
    const [payAmount, setPayAmount] = useState('');
    const [receiveAmount, setReceiveAmount] = useState('');
    const [isAutoSlippage, setIsAutoSlippage] = useState(true);
    const [slippageValue, setSlippageValue] = useState(0.5);
    const [tokens, setTokens] = useState([]);
    const { walletBalancesMap } = useBalances();
    const { data: coinInfoData } = useSubscription('coin_info');
    // Quote-related state variables
    const [quoteLoading, setQuoteLoading] = useState(false);
    const [slippageAmount, setSlippageAmount] = useState(0);
    const [minTokensReceived, setMinTokensReceived] = useState(0);
    const [sendTokenUSDValue, setSendTokenUSDValue] = useState(0);
    const [receiveTokenUSDValue, setReceiveTokenUSDValue] = useState(0);
    const [tokenRatio, setTokenRatio] = useState(0);
    // Initialize SwapGateway and hooks
    // const swapGateway = useRef(new SwapGateway()).current;
    const currentAccount = useCurrentAccount();
    const { currentWallet } = useCurrentWallet();
    const checkAddressBalanceConflict = useAddressBalanceGuard();
    const signAndExecuteTransaction = useSignAndExecuteTransaction();
    const debounceTimeout = useRef(null);
    const getQuoteTimestamp = useRef(0);
    const tokenSymbolsRef = useRef({ pay: payToken === null || payToken === void 0 ? void 0 : payToken.symbol, receive: receiveToken === null || receiveToken === void 0 ? void 0 : receiveToken.symbol });
    /** Ensure `initialReceiveSymbol` pairing runs once per modal open. */
    const initialReceivePairAppliedRef = useRef(false);
    // const swapOptions = useRef<SwapOptions | undefined>(undefined);
    const [router, setRouter] = useState(null);
    const [highlightedTokensArray, setHighlightedTokensArray] = useState([]);
    // Generic function to format token amounts based on token symbol
    const formatTokenAmount = (amount, tokenSymbol) => {
        if (tokenSymbol.toUpperCase() === 'XBTC' ||
            tokenSymbol.toUpperCase() === 'TBTC' ||
            tokenSymbol.toUpperCase() === 'WBTC' ||
            tokenSymbol.toUpperCase() === 'SUIBTC' ||
            tokenSymbol.toUpperCase() === 'BTC' ||
            tokenSymbol.toUpperCase() === 'LBTC' ||
            tokenSymbol.toUpperCase() === 'EBTC' ||
            tokenSymbol.toUpperCase() === 'XAUM' ||
            tokenSymbol.toUpperCase() === 'WETH') {
            return amount.toFixed(8);
        }
        else {
            return amount.toFixed(4);
        }
    };
    // Get quote function similar to Swap.tsx
    const getQuote = useCallback((payAmount, payToken, receiveToken) => __awaiter(void 0, void 0, void 0, function* () {
        if (!payAmount || parseFloat(payAmount) <= 0 || !payToken || !receiveToken) {
            setReceiveAmount('');
            setSlippageAmount(0);
            setMinTokensReceived(0);
            setSendTokenUSDValue(0);
            setReceiveTokenUSDValue(0);
            setTokenRatio(0);
            return;
        }
        try {
            setQuoteLoading(true);
            // Get coin objects from the SDK
            const payTokenCoin = tokens.find((token) => token.tokenId === payToken.tokenId);
            const receiveTokenCoin = tokens.find((token) => token.tokenId === receiveToken.tokenId);
            if (!payTokenCoin || !receiveTokenCoin) {
                throw new Error('Token not found in coins list');
            }
            // Calculate swap amount in raw units using shared utility
            const swapAmountWithoutDecimals = normalizeAmount(payAmount, payTokenCoin.decimals || 0);
            const alphafiSdk = createAlphaFiSDK('mainnet');
            const response = yield alphafiSdk.getCetusSwapQuote({
                from: payTokenCoin.tokenId,
                target: receiveTokenCoin.tokenId,
                amount: swapAmountWithoutDecimals,
                byAmountIn: true,
            });
            if (response) {
                setRouter(response);
                const receiveTokenAmount = response.amountOut.toString();
                const receiveAmountDecimal = convertToDecimal(receiveTokenAmount, receiveTokenCoin.decimals || 0);
                // Format receive amount using the generic function
                const receiveAmountFormatted = formatTokenAmount(receiveAmountDecimal, receiveToken.symbol);
                setReceiveAmount(receiveAmountFormatted);
                // setSlippageAmount(parseFloat((response.deviationRatio * 100).toFixed(2)));
                // Set USD values for internal calculations
                const inputUSD = parseFloat(response.amountIn.toString()) * parseFloat(payToken.price || '0') || 0;
                const outputUSD = parseFloat(response.amountOut.toString()) * parseFloat(receiveToken.price || '0') || 0;
                setSendTokenUSDValue(inputUSD);
                setReceiveTokenUSDValue(outputUSD);
                const normalizedInputUSD = inputUSD / Math.pow(10, payTokenCoin.decimals || 0);
                const normalizedOutputUSD = outputUSD / Math.pow(10, receiveTokenCoin.decimals || 0);
                setSlippageAmount(parseFloat((((normalizedOutputUSD - normalizedInputUSD) * 100) / normalizedInputUSD).toFixed(2)));
                // Calculate token ratio for internal use
                const payTokenPrice = parseFloat(payToken.price || '0');
                const receiveTokenPrice = parseFloat(receiveToken.price || '0');
                const ratio = payTokenPrice > 0 && receiveTokenPrice > 0 ? payTokenPrice / receiveTokenPrice : 0;
                const ratioFormatted = formatTokenAmount(ratio, receiveToken.symbol);
                setTokenRatio(parseFloat(ratioFormatted));
                // Calculate minimum tokens received for validation using shared utility
                const minReceived = calculateMinTokensReceived(receiveTokenAmount, slippageValue, receiveTokenCoin.decimals || 0);
                const minReceivedFormatted = formatTokenAmount(minReceived, receiveToken.symbol);
                setMinTokensReceived(parseFloat(minReceivedFormatted));
                setQuoteLoading(false);
            }
        }
        catch (error) {
            console.error('Error getting quote:', error);
            setReceiveAmount('');
            setSlippageAmount(0);
            setMinTokensReceived(0);
            setSendTokenUSDValue(0);
            setReceiveTokenUSDValue(0);
            setTokenRatio(0);
            setQuoteLoading(false);
        }
        finally {
            // setQuoteLoading(false);
        }
    }), [slippageValue, tokens]);
    const formatBalance = (rawBalance, decimals) => {
        const balance = parseFloat(rawBalance) / Math.pow(10, decimals);
        if (balance === 0)
            return '0';
        return balance.toString();
    };
    // Helper function to normalize coin types for balance lookup and format balance
    const getWalletBalance = useCallback((coinType, decimals) => {
        if (!walletBalancesMap)
            return '0';
        // Try exact match first
        let balance = walletBalancesMap.get(coinType);
        if (balance)
            return formatBalance(balance, decimals);
        // For SUI, try both formats
        if (coinType.includes('::sui::SUI')) {
            balance = walletBalancesMap.get('0x2::sui::SUI');
            if (balance)
                return formatBalance(balance, decimals);
        }
        // Try normalized format (0x0000...000002 -> 0x2)
        const normalizedType = coinType.replace(/^0x0+/, '0x');
        balance = walletBalancesMap.get(normalizedType);
        if (balance)
            return formatBalance(balance, decimals);
        return '0';
    }, [walletBalancesMap]);
    const fetchTokensWithPrices = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            if (coinInfoData) {
                const updatedTokenDetails = [];
                const coinInfoMap = new Map(Object.entries(coinInfoData));
                for (const [coinType, coinInfo] of coinInfoMap.entries()) {
                    const token = {
                        tokenId: coinType,
                        symbol: getCoinDisplayName(coinInfo.symbol),
                        icon: getIcon(coinType === wbtc_layer0_coinType
                            ? 'wbtc_layer0'
                            : coinInfo.symbol.toLowerCase(), coinType === wbtc_layer0_coinType ? 'wbtc_layer0' : coinInfo.symbol),
                        price: coinInfo.price,
                        walletBalance: getWalletBalance(coinType, parseInt(coinInfo.decimals)),
                        decimals: parseInt(coinInfo.decimals),
                    };
                    updatedTokenDetails.push(token);
                }
                // Filter out excluded tokens
                const filteredTokens = updatedTokenDetails.filter((token) => !EXCLUDED_TOKENS.includes(token.symbol));
                //set highlighted tokens in order
                const highlightedTokensBeforeOrder = filteredTokens.filter((token) => highlightedTokenSymbols.includes(token.symbol) &&
                    token.tokenId !==
                        '0x027792d9fed7f9844eb4839566001bb6f6cb4804f66aa2da6fe1ee242d896881::coin::COIN');
                const highlightedTokensInOrder = Object.fromEntries(highlightedTokenSymbols.map((key, index) => [key, index]));
                const highlightedTokens = highlightedTokensBeforeOrder.sort((tokenAObject, tokenBObject) => {
                    return (highlightedTokensInOrder[tokenAObject.symbol] -
                        highlightedTokensInOrder[tokenBObject.symbol]);
                });
                if (window.innerWidth > 768) {
                    setHighlightedTokensArray(highlightedTokens);
                }
                else {
                    const revisedHighlightedTokens = highlightedTokens.slice(0, -1);
                    setHighlightedTokensArray(revisedHighlightedTokens);
                }
                const reorderTokenData = filteredTokens.sort((a, b) => {
                    return (parseFloat(b.walletBalance || '0') * parseFloat(b.price || '0') -
                        parseFloat(a.walletBalance || '0') * parseFloat(a.price || '0'));
                });
                setTokens(reorderTokenData);
                return reorderTokenData;
            }
        }
        catch (error) {
            console.error('Error fetching tokens with prices:', error);
        }
    }), [getWalletBalance, coinInfoData]);
    // Fetch tokens on component mount and when wallet balances change
    useEffect(() => {
        fetchTokensWithPrices();
    }, [fetchTokensWithPrices]);
    // Initialize default tokens for swap when tokens are loaded (only if not already set)
    useEffect(() => {
        if (tokens.length > 1 && !payToken && !receiveToken && !initialReceiveSymbol) {
            const payToken = tokens.find((token) => token.symbol === 'SUI');
            const receiveToken = tokens.find((token) => token.symbol === 'ALPHA');
            setPayToken(payToken);
            setReceiveToken(receiveToken);
        }
    }, [tokens, payToken, receiveToken, initialReceiveSymbol]);
    // Pay SUI → receive `initialReceiveSymbol` when opening from CTAs like "Buy ALPHA"
    useEffect(() => {
        if (!isOpen) {
            initialReceivePairAppliedRef.current = false;
            return;
        }
        if (initialReceivePairAppliedRef.current || !initialReceiveSymbol || tokens.length < 2) {
            return;
        }
        const want = initialReceiveSymbol.toUpperCase();
        const targetReceive = tokens.find((t) => t.symbol.toUpperCase() === want);
        const suiPay = tokens.find((t) => t.symbol === 'SUI'); // note change this according to requirements
        if (!targetReceive || !suiPay || targetReceive.tokenId === suiPay.tokenId) {
            return;
        }
        setPayToken(suiPay);
        setReceiveToken(targetReceive);
        setPayAmount('');
        setReceiveAmount('');
        setSlippageAmount(0);
        setMinTokensReceived(0);
        setSendTokenUSDValue(0);
        setReceiveTokenUSDValue(0);
        setTokenRatio(0);
        setQuoteLoading(false);
        setIsAutoSlippage(true);
        setSlippageValue(getAutoSlippage(suiPay.symbol, targetReceive.symbol));
        initialReceivePairAppliedRef.current = true;
    }, [isOpen, initialReceiveSymbol, tokens]);
    // Update slippage automatically when tokens change and auto mode is on
    useEffect(() => {
        if (isAutoSlippage) {
            const autoSlippage = getAutoSlippage(payToken === null || payToken === void 0 ? void 0 : payToken.symbol, receiveToken === null || receiveToken === void 0 ? void 0 : receiveToken.symbol);
            setSlippageValue(autoSlippage);
        }
    }, [payToken, receiveToken, isAutoSlippage]);
    // Update selected tokens' balances when walletBalancesMap changes
    useEffect(() => {
        if (payToken && receiveToken && walletBalancesMap) {
            // Find the updated tokens with fresh balances
            const updatedPayToken = tokens.find((token) => token.tokenId === payToken.tokenId);
            const updatedReceiveToken = tokens.find((token) => token.tokenId === receiveToken.tokenId);
            if (updatedPayToken) {
                setPayToken(updatedPayToken);
            }
            if (updatedReceiveToken) {
                setReceiveToken(updatedReceiveToken);
            }
        }
    }, [walletBalancesMap, tokens, payToken, receiveToken]);
    // Debounced quote fetching when pay amount or tokens change
    useEffect(() => {
        if (!payToken || !receiveToken)
            return;
        if (payAmount && parseFloat(payAmount) > 0) {
            setQuoteLoaderState(true);
            // Clear the previous timeout if the user is still typing
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
            // Set a new timeout
            debounceTimeout.current = setTimeout(() => {
                if (Date.now() > getQuoteTimestamp.current + 500) {
                    getQuote(payAmount, payToken, receiveToken);
                    getQuoteTimestamp.current = Date.now();
                }
            }, 800); // Delay of 5000ms
        }
        else {
            setQuoteLoaderState(false);
            setReceiveAmount('');
            setSlippageAmount(0);
            setMinTokensReceived(0);
            setSendTokenUSDValue(0);
            setReceiveTokenUSDValue(0);
            setTokenRatio(0);
        }
        // Cleanup function to clear the timeout when component unmounts or dependencies change
        return () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
    }, [payAmount, payToken, receiveToken, slippageValue, getQuote]);
    const setQuoteLoaderState = (state) => {
        setQuoteLoading(state);
    };
    // Reset inputs when modal closes
    tokenSymbolsRef.current = { pay: payToken === null || payToken === void 0 ? void 0 : payToken.symbol, receive: receiveToken === null || receiveToken === void 0 ? void 0 : receiveToken.symbol };
    useEffect(() => {
        if (!isOpen) {
            setPayAmount('');
            setReceiveAmount('');
            setSlippageAmount(0);
            setMinTokensReceived(0);
            setSendTokenUSDValue(0);
            setReceiveTokenUSDValue(0);
            setTokenRatio(0);
            setQuoteLoading(false);
            setIsAutoSlippage(true);
            setSlippageValue(getAutoSlippage(tokenSymbolsRef.current.pay, tokenSymbolsRef.current.receive));
        }
    }, [isOpen]);
    const handlePayTokenSelect = (token) => {
        setPayToken(token);
        setPayAmount('');
        setReceiveAmount('');
        setSlippageAmount(0);
        setMinTokensReceived(0);
        setSendTokenUSDValue(0);
        setReceiveTokenUSDValue(0);
        setTokenRatio(0);
        setIsAutoSlippage(true);
        setSlippageValue(getAutoSlippage(token.symbol, receiveToken === null || receiveToken === void 0 ? void 0 : receiveToken.symbol));
    };
    const handleReceiveTokenSelect = (token) => {
        setReceiveToken(token);
        setPayAmount('');
        setReceiveAmount('');
        setSlippageAmount(0);
        setMinTokensReceived(0);
        setSendTokenUSDValue(0);
        setReceiveTokenUSDValue(0);
        setTokenRatio(0);
        setIsAutoSlippage(true);
        setSlippageValue(getAutoSlippage(payToken === null || payToken === void 0 ? void 0 : payToken.symbol, token.symbol));
    };
    const handlePayAmountChange = (val) => {
        // Clean input value similar to Swap.tsx
        let inputValue = val.replace(/[^0-9.]/g, '');
        inputValue = inputValue.replace(/^0+(?!\.|$)/, '');
        setPayAmount(inputValue);
        // Quote fetching is now handled by useEffect hook with debouncing
    };
    const handleDirectionSwitch = () => {
        if (payToken && receiveToken) {
            setPayToken(receiveToken);
            setReceiveToken(payToken);
            setPayAmount(receiveAmount);
            setReceiveAmount(payAmount);
        }
    };
    const handleSwap = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        if (!payToken || !receiveToken || !router || !(currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.address))
            return;
        if (checkAddressBalanceConflict([{ coinType: payToken.tokenId }])) {
            onNotification({
                title: 'Wallet Update Required',
                description: `${(_a = currentWallet === null || currentWallet === void 0 ? void 0 : currentWallet.name) !== null && _a !== void 0 ? _a : 'Your wallet'} doesn't yet support address balances, a recent Sui network update. Your funds are safe — to continue, connect a supported wallet.`,
                spinner: false,
                image: getIcon('failed'),
            });
            return;
        }
        try {
            setLoading(true);
            // Show processing notification
            onNotification({
                title: 'Processing Swap',
                description: `Requesting wallet to approve`,
                spinner: true,
                image: null,
            });
            const alphafiSdk = createAlphaFiSDK('mainnet');
            const txb = yield alphafiSdk.cetusSwapTxb({
                router,
                slippage: slippageValue / 100,
            });
            if (txb) {
                // Check SUI balance for gas
                const suiToken = tokens.find((token) => token.symbol === 'SUI');
                const suiBalance = parseFloat((suiToken === null || suiToken === void 0 ? void 0 : suiToken.walletBalance) || '0');
                const gasAmount = Number((_b = txb.getData().gasData.budget) !== null && _b !== void 0 ? _b : 1e8) / 1e9; // Convert to SUI units
                if (gasAmount > suiBalance) {
                    onNotification({
                        title: 'Insufficient SUI for Gas',
                        description: 'Please add more SUI to your wallet to proceed.',
                        spinner: false,
                        image: getIcon('failed'),
                    });
                    setLoading(false);
                    return;
                }
                signAndExecuteTransaction({
                    transaction: txb,
                }, {
                    onSuccess: (result) => __awaiter(void 0, void 0, void 0, function* () {
                        onNotification({
                            title: 'Broadcasting Transaction',
                            description: 'Broadcasting transaction...',
                            spinner: true,
                            image: null,
                        });
                        try {
                            if (result.success) {
                                onNotification({
                                    title: 'Swap Successful',
                                    description: `Successfully swapped ${payAmount} ${getCoinDisplayName(payToken.symbol)} to ${receiveAmount} ${getCoinDisplayName(receiveToken.symbol)}`,
                                    spinner: false,
                                    image: getIcon('success'),
                                    link: `https://suiscan.xyz/mainnet/tx/${result.digest}`,
                                });
                                // Clear input fields and close modal
                                setPayAmount('');
                                setReceiveAmount('');
                                setSlippageAmount(0);
                                setMinTokensReceived(0);
                                setSendTokenUSDValue(0);
                                setReceiveTokenUSDValue(0);
                                setTokenRatio(0);
                                // onClose();
                            }
                            else {
                                onNotification({
                                    title: 'Swap Failed',
                                    description: `Your swap of ${payAmount} ${getCoinDisplayName(payToken.symbol)} to ${receiveAmount} ${getCoinDisplayName(receiveToken.symbol)} has failed.`,
                                    spinner: false,
                                    image: getIcon('failed'),
                                    link: `https://suiscan.xyz/mainnet/tx/${result.digest}`,
                                });
                            }
                        }
                        catch (waitError) {
                            console.error('Error waiting for transaction:', waitError);
                            onNotification({
                                title: 'Transaction Status Unknown',
                                description: 'Unable to confirm transaction status. Please check your wallet.',
                                spinner: false,
                                image: getIcon('failed'),
                            });
                        }
                        setLoading(false);
                    }),
                    onError: (err) => __awaiter(void 0, void 0, void 0, function* () {
                        console.error('Transaction error:', err);
                        let title = 'Swap Failed';
                        let description = 'Something went wrong. Please try again later or contact us if the issue persists.';
                        if (err.message === 'User rejected the request.') {
                            title = 'Transaction Rejected';
                            description = `You have rejected the transaction to swap ${payAmount} ${getCoinDisplayName(payToken.symbol)} to ${receiveAmount} ${getCoinDisplayName(receiveToken.symbol)}`;
                        }
                        onNotification({
                            title,
                            description,
                            spinner: false,
                            image: getIcon('failed'),
                        });
                        setLoading(false);
                    }),
                });
            }
        }
        catch (error) {
            console.error('Swap preparation failed:', error);
            onNotification({
                title: 'Swap Failed',
                description: 'Failed to prepare swap transaction. Please try again.',
                spinner: false,
                image: getIcon('failed'),
            });
            setLoading(false);
        }
    });
    const handleRefresh = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            // Set loading state to prevent multiple refresh calls
            setQuoteLoading(true);
            // Refresh token prices first
            yield fetchTokensWithPrices();
            // Refetch quote if there's a valid pay amount and tokens selected
            if (payAmount && parseFloat(payAmount) > 0 && payToken && receiveToken) {
                yield getQuote(payAmount, payToken, receiveToken);
            }
        }
        catch (error) {
            console.error('Error during refresh:', error);
            // Optionally show a notification about refresh failure
            onNotification({
                title: 'Refresh Failed',
                description: 'Failed to refresh prices and quotes. Please try again.',
                spinner: false,
                image: getIcon('failed'),
            });
        }
        finally {
            setQuoteLoading(false);
        }
    });
    if (!payToken || !receiveToken) {
        return null;
    }
    // Modified wallet balance for SUI by maintaining required gas fees
    const adjustedPayToken = payToken.symbol.toLowerCase() === 'sui'
        ? Object.assign(Object.assign({}, payToken), { walletBalance: Math.max(0, parseFloat(payToken.walletBalance || '0') - 0.05).toFixed(6) }) : payToken;
    return (_jsx(Swap, { title: "Swap", payAmount: payAmount, swapIcon: theme === 'dark' ? getIcon('whiteSwapIcon') : getIcon('blackSwapIcon'), downArrow: theme === 'dark' ? getIcon('whiteDownArrow') : getIcon('blackDownArrow'), slippageValue: slippageValue, slippagePercentage: slippageAmount, onSlippageChange: (value) => {
            setSlippageValue(value);
        }, isAutoSlippage: isAutoSlippage, onAutoSlippageChange: (isAuto) => {
            setIsAutoSlippage(isAuto);
            if (isAuto) {
                setSlippageValue(getAutoSlippage(payToken === null || payToken === void 0 ? void 0 : payToken.symbol, receiveToken === null || receiveToken === void 0 ? void 0 : receiveToken.symbol));
            }
        }, autoSlippageValue: getAutoSlippage(payToken === null || payToken === void 0 ? void 0 : payToken.symbol, receiveToken === null || receiveToken === void 0 ? void 0 : receiveToken.symbol), slippageIcon: theme === 'dark' ? getIcon('whiteSlippageIcon') : getIcon('blackSlippageIcon'), refreshIcon: theme === 'dark' ? getIcon('whiteReloadIcon') : getIcon('blackReloadIcon'), onRefresh: handleRefresh, viceVersaIcon: theme === 'dark' ? getIcon('viceVersaIconWhite') : getIcon('viceVersaIconBlack'), receiveAmount: receiveAmount, onPayAmountChange: handlePayAmountChange, payToken: adjustedPayToken, payBalance: payToken && payToken.symbol.toLowerCase() === 'sui'
            ? Math.max(0, parseFloat(payToken.walletBalance || '0') - 0.05).toString() || '0'
            : payToken.walletBalance || '0', receiveBalance: receiveToken.walletBalance || '0', highlightedTokens: highlightedTokensArray, closeIcon: theme === 'dark' ? getIcon('whiteCloseIcon') : getIcon('blackCloseIcon'), walletIcon: theme === 'dark' ? getIcon('darkWalletIcon') : getIcon('lightWalletIcon'), receiveToken: receiveToken, onPayTokenSelect: handlePayTokenSelect, onReceiveTokenSelect: handleReceiveTokenSelect, tokens: tokens, isOpen: isOpen, onClose: onClose, onSwap: handleSwap, swapDisabled: !payAmount ||
            parseFloat(payAmount) <= 0 ||
            loading ||
            quoteLoading ||
            !currentAccount ||
            parseFloat(payAmount) > parseFloat(payToken.walletBalance || '0') ||
            parseFloat(receiveAmount) <= 0 ||
            minTokensReceived <= 0 ||
            (slippageAmount < 0 && Math.abs(slippageAmount) > slippageValue) ||
            (sendTokenUSDValue > 0 && receiveTokenUSDValue <= 0) ||
            tokenRatio <= 0, onDirectionSwitch: handleDirectionSwitch, poweredBy: "", clearSearchIcon: getIcon('clearSearchIcon'), searchIcon: theme === 'dark' ? getIcon('searchDark') : getIcon('defaultThemeSearchIcon'), backIcon: theme === 'dark' ? getIcon('whiteBackIcon') : getIcon('blackBackIcon'), walletConnectFunctionality: walletConnectFunctionality, isWalletConnected: isWalletConnected, minTokensReceived: minTokensReceived, tokenRatio: tokenRatio, loading: loading, quoteLoading: quoteLoading }));
};
export default SwapModal;
